import { toast } from 'react-toastify';

// Toast categories for separate queue management
export const TOAST_CATEGORIES = {
  LOBBY_JOIN: 'lobby_join',
  SCREEN_SHARE: 'screen_share',
  RECORDING: 'recording',
  PARTICIPANT_ACTIONS: 'participant_actions',
  ERROR: 'error',
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  GENERAL: 'general'
};

// Default configuration
const DEFAULT_CONFIG = {
  suppressionWindow: 2000, // 2 seconds
  maxQueueSize: 10, // Maximum items in queue
  enableDeduplication: true, // Merge duplicate toasts
  enableQueue: true // Enable/disable queue system
};

class ToastQueueManager {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.queues = new Map(); // Separate queue for each category
    this.activeToasts = new Map(); // Track active toasts by category
    this.suppressionTimers = new Map(); // Track suppression windows
    this.isProcessing = false;
    
    // Initialize queues for each category
    Object.values(TOAST_CATEGORIES).forEach(category => {
      this.queues.set(category, []);
      this.activeToasts.set(category, null);
      this.suppressionTimers.set(category, null);
    });
  }

  // Generate a unique key for toast deduplication
  generateToastKey(type, message, category) {
    return `${category}_${type}_${this.hashString(message)}`;
  }

  // Simple string hash function
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  // Check if toast is duplicate
  isDuplicate(toastItem, category) {
    if (!this.config.enableDeduplication) return false;
    
    const queue = this.queues.get(category) || [];
    const activeToast = this.activeToasts.get(category);
    
    // Check active toast
    if (activeToast && activeToast.key === toastItem.key) {
      return true;
    }
    
    // Check queue
    return queue.some(item => item.key === toastItem.key);
  }

  // Add toast to queue
  addToQueue(toastItem, category) {
    const queue = this.queues.get(category) || [];
    
    // Check queue size limit
    if (queue.length >= this.config.maxQueueSize) {
      console.warn(`Toast queue for category ${category} is full. Dropping oldest item.`);
      queue.shift(); // Remove oldest item
    }
    
    queue.push(toastItem);
    this.queues.set(category, queue);
  }

  // Process next toast in queue
  processNextToast(category) {
    const queue = this.queues.get(category) || [];
    
    if (queue.length === 0) {
      // No more toasts in queue, clear active state
      this.activeToasts.set(category, null);
      this.suppressionTimers.set(category, null);
      return;
    }
    
    const nextToast = queue.shift();
    this.queues.set(category, queue);
    
    // Show the toast
    this.showToast(nextToast, category);
  }

  // Show toast and set up suppression window
  showToast(toastItem, category) {
    const { type, message, options = {} } = toastItem;
    
    // Set default options
    const defaultOptions = {
      position: "top-right",
      autoClose: this.config.suppressionWindow,
      hideProgressBar: true,
      theme: "colored",
      onClose: () => {
        // When toast closes, process next in queue
        this.onToastClose(category);
      }
    };
    
    const finalOptions = { ...defaultOptions, ...options };
    
    // Show the toast
    const toastId = toast[type](message, finalOptions);
    
    // Track active toast
    this.activeToasts.set(category, {
      id: toastId,
      key: toastItem.key,
      timestamp: Date.now()
    });
    
    // Set suppression timer
    const timer = setTimeout(() => {
      this.onToastClose(category);
    }, this.config.suppressionWindow);
    
    this.suppressionTimers.set(category, timer);
  }

  // Handle toast close
  onToastClose(category) {
    // Clear suppression timer
    const timer = this.suppressionTimers.get(category);
    if (timer) {
      clearTimeout(timer);
      this.suppressionTimers.set(category, null);
    }
    
    // Process next toast in queue
    this.processNextToast(category);
  }

  // Main method to show toast with queue management
  show(type, message, options = {}, category = TOAST_CATEGORIES.GENERAL) {
    if (!this.config.enableQueue) {
      // Queue disabled, show toast directly
      return toast[type](message, options);
    }
    
    const toastItem = {
      type,
      message,
      options,
      key: this.generateToastKey(type, message, category),
      timestamp: Date.now()
    };
    
    // Check if category is currently suppressed
    const isSuppressed = this.activeToasts.get(category) !== null;
    
    if (isSuppressed) {
      // Category is suppressed, add to queue
      if (!this.isDuplicate(toastItem, category)) {
        this.addToQueue(toastItem, category);
      }
      return null;
    } else {
      // No suppression, show toast immediately
      this.showToast(toastItem, category);
      return toastItem.key;
    }
  }

  // Convenience methods for different toast types
  success(message, options = {}, category = TOAST_CATEGORIES.SUCCESS) {
    return this.show('success', message, options, category);
  }

  error(message, options = {}, category = TOAST_CATEGORIES.ERROR) {
    return this.show('error', message, options, category);
  }

  info(message, options = {}, category = TOAST_CATEGORIES.INFO) {
    return this.show('info', message, options, category);
  }

  warning(message, options = {}, category = TOAST_CATEGORIES.WARNING) {
    return this.show('warning', message, options, category);
  }

  // Clear all queues and active toasts
  clearAll() {
    this.queues.forEach((queue, category) => {
      this.queues.set(category, []);
    });
    
    this.activeToasts.forEach((activeToast, category) => {
      if (activeToast) {
        toast.dismiss(activeToast.id);
      }
      this.activeToasts.set(category, null);
    });
    
    this.suppressionTimers.forEach((timer, category) => {
      if (timer) {
        clearTimeout(timer);
      }
      this.suppressionTimers.set(category, null);
    });
  }

  // Clear specific category
  clearCategory(category) {
    this.queues.set(category, []);
    
    const activeToast = this.activeToasts.get(category);
    if (activeToast) {
      toast.dismiss(activeToast.id);
      this.activeToasts.set(category, null);
    }
    
    const timer = this.suppressionTimers.get(category);
    if (timer) {
      clearTimeout(timer);
      this.suppressionTimers.set(category, null);
    }
  }

  // Get queue status
  getQueueStatus() {
    const status = {};
    this.queues.forEach((queue, category) => {
      status[category] = {
        queueLength: queue.length,
        hasActiveToast: this.activeToasts.get(category) !== null,
        isSuppressed: this.suppressionTimers.get(category) !== null
      };
    });
    return status;
  }

  // Update configuration
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  // Manual dismiss - immediately show next toast
  dismiss(category) {
    const activeToast = this.activeToasts.get(category);
    if (activeToast) {
      toast.dismiss(activeToast.id);
      this.onToastClose(category);
    }
  }
}

// Create singleton instance
const toastQueue = new ToastQueueManager();

// Export the singleton and helper functions
export default toastQueue;

// Helper function to create category-specific toast functions
export const createCategoryToast = (category) => ({
  success: (message, options = {}) => toastQueue.success(message, options, category),
  error: (message, options = {}) => toastQueue.error(message, options, category),
  info: (message, options = {}) => toastQueue.info(message, options, category),
  warning: (message, options = {}) => toastQueue.warning(message, options, category),
  dismiss: () => toastQueue.dismiss(category)
});

// Pre-configured toast functions for common categories
export const lobbyToast = createCategoryToast(TOAST_CATEGORIES.LOBBY_JOIN);
export const screenShareToast = createCategoryToast(TOAST_CATEGORIES.SCREEN_SHARE);
export const recordingToast = createCategoryToast(TOAST_CATEGORIES.RECORDING);
export const participantToast = createCategoryToast(TOAST_CATEGORIES.PARTICIPANT_ACTIONS);
export const errorToast = createCategoryToast(TOAST_CATEGORIES.ERROR);
export const successToast = createCategoryToast(TOAST_CATEGORIES.SUCCESS);
export const infoToast = createCategoryToast(TOAST_CATEGORIES.INFO);
export const warningToast = createCategoryToast(TOAST_CATEGORIES.WARNING);
